import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, ViewChild, inject } from '@angular/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { SharedModule } from 'src/app/shared/shared.module';
import { AuthStore } from 'src/app/store/auth.store';
import { DebtsStore } from 'src/app/store/debts.store';
import { PWAStore } from 'src/app/store/pwa.store';
import { SiteStore } from 'src/app/store/site.store';
import { environment } from 'src/environments/environment';
import { GradientBackgroundComponent } from "../../gradient-background/gradient-background.component";
import { EmailIconComponent } from "../../icons/email-icon.component";
import { LogoutIconComponent } from "../../icons/logout-icon.component";
import { QrIconComponent } from "../../icons/qr-icon.component";
import { SecurityIconComponent } from "../../icons/security-icon.component";
import { NotificationIconComponent } from "../../icons/notification-icon.component";
import { AnnouncementIconComponent } from "../../icons/announcement-icon.component";
import { AppUpdateIconComponent } from "../../icons/app-update-icon.component";
import { AuctionIconComponent } from "../../icons/auction-icon.component";
import { StatisticsIconComponent } from "../../icons/statistics-icon.component";
import { BagIconComponent } from "../../icons/bag-icon.component";
import { ApiIconComponent } from "../../icons/api-icon.component";
import { TelegramIconComponent } from "../../icons/telegram-icon.component";
import { UserIconComponent } from "../../icons/user-icon.component";
import { HelpIconComponent } from "../../icons/help-icon.component";
import { SupportChatIconComponent } from "../../icons/support-chat-icon.component";
import { NotificationStore } from "src/app/store/notification.store";
import { AppUpdatesStore } from "src/app/store/app-updates.store";
import { AppUpdatePopupService } from "src/app/services/app-update-popup.service";
import { UpdatePwaButton } from "../../update-pwa-button.component";
import { SupportChatStore } from "src/app/pages/support-chat/services/support-chat.store";
import { BankAccountsStore } from "src/app/store/bank-accounts.store";
import { ActivityHeartbeatService } from "src/app/services/activity-heartbeat.service";

@Component({
	selector: 'app-drawer',
	standalone: true,
	imports: [
    MatSidenavModule, SharedModule,
    QrIconComponent,
    UserIconComponent,
    SecurityIconComponent,
    NotificationIconComponent,
    AnnouncementIconComponent,
    AppUpdateIconComponent,
    AuctionIconComponent,
    StatisticsIconComponent,
    BagIconComponent,
    ApiIconComponent,
    LogoutIconComponent,
    UpdatePwaButton,
    EmailIconComponent,
    TelegramIconComponent,
    GradientBackgroundComponent,
    HelpIconComponent,
    SupportChatIconComponent
],
	templateUrl: './drawer.component.html',
	styleUrl: './drawer.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DrawerComponent implements OnInit, OnDestroy {
	@ViewChild('drawer') drawer

	appVersion = environment.appVersion
	siteStore = inject(SiteStore)
	authStore = inject(AuthStore)
	pwaStore = inject(PWAStore);
	debtsStore = inject(DebtsStore);
	notificationStore = inject(NotificationStore);
	/** Latido de presencia: alimenta el Dashboard de actividad del panel admin. */
	private activityHeartbeat = inject(ActivityHeartbeatService);
	appUpdatesStore = inject(AppUpdatesStore);
	appUpdatePopupService = inject(AppUpdatePopupService);
	supportChatStore = inject(SupportChatStore);
	bankAccountsStore = inject(BankAccountsStore);
	snackBar = inject(MatSnackBar);
	router = inject(Router);

	private popupNavSub?: Subscription;

	toggleDrawer(): void {
		this.drawer.toggle();
	}
	closeDrawer() {
		this.siteStore.openDrawer.set(false)
	}

	logout() {
		this.closeDrawer()
		this.authStore.logout()
	}

	// Opens the floating support-chat widget. The widget is rendered only on
	// /fund-account/crypto, so when the user is elsewhere we navigate there
	// first and open the chat after the navigation resolves — otherwise the
	// widget host wouldn't be mounted yet when openWidget() fires.
	openSupportChat() {
		this.closeDrawer();
		const targetRoute = '/fund-account/crypto';
		const currentRoute = this.router.url.split('?')[0].split('#')[0];
		if (currentRoute === targetRoute) {
			this.supportChatStore.openWidget();
			return;
		}
		this.router.navigateByUrl(targetRoute).then((ok) => {
			if (ok) this.supportChatStore.openWidget();
		});
	}

	ngOnInit(): void {
		this.notificationStore.startPolling();
		this.activityHeartbeat.start();
		// Show any pending popup announcements. The Novedades unread counts are
		// loaded by AppUpdatesStore's own effect() when the auth token is present,
		// so we don't call loadUnreadCounts() here (would duplicate the request).
		this.appUpdatePopupService.showPendingPopups();
		this.appUpdatesStore.loadOffers();

		// Also surface popups when the user navigates back to the home route
		// while already inside the app: a new announcement published mid-session
		// then shows on the next visit to `/`. The service dedupes (only unread
		// announcements are returned, each marked read on dismiss) and guards
		// against overlapping runs, so this never re-shows a seen popup.
		this.popupNavSub = this.router.events
			.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
			.subscribe((e) => {
				const path = (e.urlAfterRedirects || e.url).split('?')[0].split('#')[0];
				if (path === '/') {
					this.appUpdatePopupService.showPendingPopups();
					this.appUpdatesStore.loadOffers();
				}
			});
		// Load pending debts when the component initializes
		// this.debtsStore.loadPendingDebts().then(hasDebts => {
		// 	// If there are pending debts, show a notification
		// 	if (this.debtsStore.hasPendingDebts()) {
		// 		// Get the count of pending debts
		// 		const pendingCount = this.debtsStore.pendingDebts().length;
		// 		// Show a snackbar with the count and a button to navigate to debts
		// 		const snackBarRef = this.snackBar.open(
		// 			`Tienes ${pendingCount} deuda${pendingCount !== 1 ? 's' : ''} pendiente${pendingCount !== 1 ? 's' : ''}`,
		// 			'Ver deudas',
		// 			{
		// 				duration: 30000,
		// 				panelClass: 'mat-snack-warn',
		// 				horizontalPosition: 'center',
		// 				verticalPosition: 'bottom'
		// 			}
		// 		);

		// 		// Navigate to debts page when action button is clicked
		// 		snackBarRef.onAction().subscribe(() => {
		// 			this.router.navigate(['/debts']);
		// 		});
		// 	}
		// });
	}

	ngOnDestroy(): void {
		this.notificationStore.stopPolling();
		this.activityHeartbeat.stop();
		this.popupNavSub?.unsubscribe();
	}
}
